import type { EventsState } from "@/context/events";

import {
	isLocationZoneSensorFilterActive,
	type LocationZoneSensorIdSets,
} from "@/features/events/utils/filter-valid-ids";
import { isSeverityFilterActive } from "@/features/events/utils/severity-filter-utils";

export type AreEventFiltersActiveOptions = {
	/** True when a search draft is typed but not yet submitted (e.g. SearchCommandMenu). */
	hasLocalSearchDraft?: boolean;
	/** When true, a non-null alert status counts as active (All Activity reset). */
	includeAlertStatus?: boolean;
};

export type EventFiltersActiveState = Pick<
	EventsState,
	| "dateRangeFilter"
	| "eventTypesFilter"
	| "locationsFilter"
	| "searchQueryFilter"
	| "sensorsFilter"
	| "severityFilter"
	| "statusFilter"
	| "zonesFilter"
>;

/**
 * Whether any event filter differs from its defaults.
 * Shared by `useEvents().hasActiveFilters` and `useCanResetEventFilters`.
 */
export function areEventFiltersActive(
	state: EventFiltersActiveState,
	idSets: LocationZoneSensorIdSets,
	options?: AreEventFiltersActiveOptions,
): boolean {
	const { hasLocalSearchDraft = false, includeAlertStatus = false } =
		options ?? {};
	const isSearchActive =
		hasLocalSearchDraft || state.searchQueryFilter.trim() !== "";

	return (
		isSearchActive ||
		state.eventTypesFilter.length > 0 ||
		isSeverityFilterActive(state.severityFilter) ||
		(includeAlertStatus && state.statusFilter !== null) ||
		state.dateRangeFilter !== null ||
		isLocationZoneSensorFilterActive(state, idSets)
	);
}
